import React from "react";
import { 
  BarChart3, Calendar, FileText, Zap, ExternalLink,
  TrendingUp, AlertTriangle, CheckCircle2, Clock, 
  Plus, Link2, ShieldCheck, ArrowRight, Play, Eye,
  Search, Globe, Sparkles, RefreshCw
} from "lucide-react";
import { SeoMarket, ContentItem, BacklinkItem, KeywordItem, CompetitorItem, PusherSettings, PusherState, SeoOverview } from "./types";
import PusherTimeline from "./PusherTimeline";
import { DAY_LABEL, MARKET_TZ, MODE_LABEL, fmtDateTime, fmtDay, localParts } from "./engineFormat";

const JOB_LABEL: Record<string, string> = {
  publisher_tick: "Pusher", news_agent: "Nyhedsagent", rank_post: "Rank (bestil)", rank_collect: "Rank (hent)",
  gsc_sync: "Search Console", volume_refresh: "Søgevolumen", radar_sitemaps: "Radar: sitemaps",
  radar_offers: "Radar: partnere", radar_reviews: "Radar: anmeldelser", radar_backlinks: "Radar: backlinks",
  generate: "Generér nu",
};

interface SeoOverviewTabProps {
  market: SeoMarket;
  contentList: ContentItem[];
  backlinksList: BacklinkItem[];
  keywordsList: KeywordItem[];
  competitorsList: CompetitorItem[];
  settings: PusherSettings;
  onTabChange: (tab: any) => void;
  onTriggerPush: () => void;
  isDeploying: boolean;
  readyBufferCount: number;
  daysBufferRemaining: number;
  isBufferLow: boolean;
  engineConnected?: boolean;
  pusherState?: PusherState | null;
  overview?: SeoOverview | null;
}

export default function SeoOverviewTab({
  market,
  contentList,
  backlinksList,
  keywordsList,
  competitorsList,
  settings,
  onTabChange,
  onTriggerPush,
  isDeploying,
  readyBufferCount,
  daysBufferRemaining,
  isBufferLow,
  engineConnected = false,
  pusherState = null,
  overview = null
}: SeoOverviewTabProps) {
  const tz = MARKET_TZ[market];
  const next = pusherState?.nextSlot;
  const nextText = next
    ? `Næste udgivelse: ${fmtDay(next.date)} kl. ${localParts(next.plannedAt, tz).hhmm}${next.title ? "" : " (ledig slot)"}`
    : engineConnected ? "Ingen slot planlagt" : "Kalenderen beregnes af backend";
  const pusherText = pusherState
    ? `Pusher ${pusherState.settings.enabled ? "aktiv" : "slået fra"} · ${pusherState.settings.activeDays
        .map((d) => DAY_LABEL[d] || d).join(" & ")} ${pusherState.settings.windowStart}–${pusherState.settings.windowEnd}` +
      ` · jitter ±${pusherState.settings.jitterMinMinutes ?? 30}–${pusherState.settings.jitterMaxMinutes ?? 75} min · ` +
      `${MODE_LABEL[pusherState.mode] || pusherState.mode}`
    : "SEO-motoren er ikke forbundet, så der udgives intet automatisk.";
  const publishedCount = contentList.filter(c => c.status === "published").length;
  const reviewCount = contentList.filter(c => c.status === "review").length;
  const draftCount = contentList.filter(c => c.status === "draft").length;
  const ideaCount = contentList.filter(c => c.status === "idea").length;

  const liveBacklinks = backlinksList.filter(b => b.status === "live_verified");
  const orderedBacklinks = backlinksList.filter(b => b.status === "in_progress");
  const activeOrOrderedBacklinks = [...liveBacklinks, ...orderedBacklinks];
  const avgDr = activeOrOrderedBacklinks.length > 0
    ? Math.round(activeOrOrderedBacklinks.reduce((acc, b) => acc + b.domainRating, 0) / activeOrOrderedBacklinks.length)
    : 0;

  const topKeywords = [...keywordsList]
    .filter(k => k.currentPosition !== null)
    .sort((a, b) => (a.currentPosition || 99) - (b.currentPosition || 99))
    .slice(0, 5);

  const marketNames: Record<SeoMarket, { name: string; flag: string }> = {
    SE: { name: "Sverige", flag: "🇸🇪" },
    NO: { name: "Norge", flag: "🇳🇴" },
    FI: { name: "Finland", flag: "🇫🇮" },
    DK: { name: "Danmark", flag: "🇩🇰" },
    DE: { name: "Tyskland", flag: "🇩🇪" },
    ZA: { name: "Sydafrika", flag: "🇿🇦" },
    FR: { name: "Frankrig", flag: "🇫🇷" },
    ES: { name: "Spanien", flag: "🇪🇸" }
  };

  return (
    <div className="space-y-6">
      
      {/* Buffer Alert Banner (Syncs with Daily Reports) */}
      {isBufferLow ? (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-amber-900 shadow-xs">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-sm">
                ⚠️ SimplyLoan Buffer Alert ({marketNames[market].flag} {marketNames[market].name}): Kun {readyBufferCount} artikler klar i køen!
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Din buffer dækker ca. {daysBufferRemaining} dages udgivelser. Målet er mindst {settings.targetBufferMinDays} dage. Denne advarsel fremgår også i Daily Reports.
              </div>
            </div>
          </div>
          <button 
            onClick={() => onTabChange("content")}
            className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl transition shrink-0 flex items-center justify-center gap-1.5"
          >
            <span>Fyld bufferen op</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-emerald-900 text-sm shadow-xs">
          <div className="flex items-center gap-2.5 font-medium">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>
              <strong>Buffer-status er sund ({marketNames[market].flag}):</strong> {readyBufferCount} godkendte artikler klar i køen ({daysBufferRemaining} dages dækning).
            </span>
          </div>
          <span className="text-xs font-bold text-emerald-700 bg-white px-2.5 py-1 rounded-md border border-emerald-200 self-start sm:self-auto">
            {nextText}
          </span>
        </div>
      )}

      {/* 4 Core Metric KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Card 1: Buffer Pipeline */}
        <div 
          onClick={() => onTabChange("content")}
          className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs space-y-1 hover:border-gray-200 cursor-pointer transition"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Buffer Pipeline</div>
            <Calendar className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-gray-900 flex items-baseline gap-2">
            <span>{readyBufferCount}</span>
            <span className="text-xs font-medium text-gray-500">artikler klar</span>
          </div>
          <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
            <span>≈ {daysBufferRemaining} dages auto-udgivelse</span>
          </div>
        </div>

        {/* Card 2: Live Articles */}
        <div 
          onClick={() => onTabChange("content")}
          className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs space-y-1 hover:border-gray-200 cursor-pointer transition"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Live Indhold</div>
            <FileText className="w-4 h-4 text-[#0F3F34]" />
          </div>
          <div className="text-2xl font-black text-gray-900">
            {publishedCount}
          </div>
          <div className="text-xs text-gray-500">
            Artikler og guides online
          </div>
        </div>

        {/* Card 3: Top Rank Keywords */}
        <div 
          onClick={() => onTabChange("keywords")}
          className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs space-y-1 hover:border-gray-200 cursor-pointer transition"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tracked Keywords</div>
            <TrendingUp className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-gray-900 flex items-baseline gap-2">
            <span>{keywordsList.length}</span>
            <span className="text-xs font-medium text-emerald-600">
              {keywordsList.filter(k => (k.currentPosition || 99) <= 10).length} i Top 10
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Organisk synlighed i Google
          </div>
        </div>

        {/* Card 4: Backlinks & DR */}
        <div 
          onClick={() => onTabChange("backlinks")}
          className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs space-y-1 hover:border-gray-200 cursor-pointer transition"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Backlinks (WhitePress)</div>
            <Link2 className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="text-2xl font-black text-gray-900 flex items-baseline gap-2">
            <span>{activeOrOrderedBacklinks.length}</span>
            {avgDr > 0 && <span className="text-xs font-medium text-indigo-600">Gns. DR {avgDr}</span>}
          </div>
          <div className="text-xs text-gray-500">
            {orderedBacklinks.length > 0
              ? `${orderedBacklinks.length} bestilt (${liveBacklinks.length} live)`
              : `${liveBacklinks.length} verificerede links`}
          </div>
        </div>
      </div>

      {/* Shortcut Banner to Roadmap & Gantt (Q4 2026 - Q1 2027) */}
      <div
        onClick={() => onTabChange("roadmap")}
        className="bg-gradient-to-r from-[#0F3F34] to-[#165A4B] p-4 rounded-2xl text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:opacity-95 transition shadow-xs"
      >
        <div className="flex items-center gap-3">
          <span className="px-2.5 py-1 rounded-full bg-emerald-400/20 text-emerald-200 text-xs font-bold shrink-0">
            ✓ Styr på alt (Uge 45)
          </span>
          <div className="text-sm">
            <strong>Nordisk SEO Masterplan &amp; Gantt (Nov 2026 – Q1 2027):</strong> Push 0 &amp; Push 1 er live · 3 SE WhitePress-links bestilt (3. nov kl. 16:36) · 0 opgaver mangler dig i dag.
          </div>
        </div>
        <span className="text-xs font-bold bg-white text-[#0F3F34] px-3.5 py-1.5 rounded-xl shrink-0 flex items-center gap-1.5 self-start sm:self-auto">
          <span>Åbn Roadmap &amp; Gantt</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>

      {/* Grid: Content Funnel + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Content Pipeline Funnel (2 Cols) */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#0F3F34]" />
                <span>Content Funnel ({marketNames[market].flag} {marketNames[market].name})</span>
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Artiklernes flow fra research til publicering på SimplyLoan.
              </p>
            </div>
            <button
              onClick={() => onTabChange("content")}
              className="text-xs font-bold text-[#0F3F34] hover:underline flex items-center gap-1"
            >
              <span>Se kalender</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Flow Blocks */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
            <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-100 text-center space-y-1">
              <span className="text-[10px] font-bold uppercase text-gray-400">1. Idéer</span>
              <div className="text-xl font-black text-gray-700">{ideaCount}</div>
              <span className="text-[10px] text-gray-400">Under emne-research</span>
            </div>

            <div className="bg-amber-50/60 p-3.5 rounded-xl border border-amber-100 text-center space-y-1">
              <span className="text-[10px] font-bold uppercase text-amber-600">2. Kladder</span>
              <div className="text-xl font-black text-amber-800">{draftCount}</div>
              <span className="text-[10px] text-amber-600">AI generation</span>
            </div>

            <div className="bg-purple-50/60 p-3.5 rounded-xl border border-purple-100 text-center space-y-1">
              <span className="text-[10px] font-bold uppercase text-purple-600">3. Review</span>
              <div className="text-xl font-black text-purple-800">{reviewCount}</div>
              <span className="text-[10px] text-purple-600">Faktatjek & polish</span>
            </div>

            <div className="bg-emerald-50 p-3.5 rounded-xl border border-emerald-200 text-center space-y-1 ring-1 ring-emerald-500/20">
              <span className="text-[10px] font-bold uppercase text-emerald-700 font-mono">4. Buffer</span>
              <div className="text-xl font-black text-emerald-800">{readyBufferCount}</div>
              <span className="text-[10px] text-emerald-700 font-semibold">Godkendt</span>
            </div>

            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-center space-y-1 col-span-2 sm:col-span-1">
              <span className="text-[10px] font-bold uppercase text-slate-500">5. Live</span>
              <div className="text-xl font-black text-[#0F3F34]">{publishedCount}</div>
              <span className="text-[10px] text-slate-500">På SimplyLoan</span>
            </div>
          </div>

          {/* Quick Push Callout */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0F3F34]" />
              <span className="text-gray-700">{pusherText}</span>
            </div>
            <button
              onClick={onTriggerPush}
              disabled={isDeploying || readyBufferCount === 0 || !engineConnected}
              title={engineConnected ? "Udgiver næste godkendte artikel uden for kalenderen" : "Kræver forbindelse til SEO-motoren"}
              className="px-3 py-1.5 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-1.5 shrink-0"
            >
              {isDeploying ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Udgiver...</span>
                </>
              ) : (
                <>
                  <Play className="w-3 h-3 fill-white" />
                  <span>Skub næste artikel nu</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Quick System Actions & Free Tools (1 Col) */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <span>SEO Værktøjer & Genveje</span>
          </h3>

          <div className="space-y-2 text-xs">
            <button
              onClick={() => onTabChange("technical")}
              className="w-full text-left p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition flex items-center justify-between border border-gray-100"
            >
              <div>
                <div className="font-bold text-gray-900">Google Search Console</div>
                <div className="text-[11px] text-gray-500">Tjek indexering & gratis søgeord</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </button>

            <button
              onClick={() => onTabChange("competitors")}
              className="w-full text-left p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition flex items-center justify-between border border-gray-100"
            >
              <div>
                <div className="font-bold text-gray-900">Meta & Google Ads Spionage</div>
                <div className="text-[11px] text-gray-500">Se Lendo & Samblas aktive kampagner</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </button>

            <button
              onClick={() => onTabChange("technical")}
              className="w-full text-left p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition flex items-center justify-between border border-gray-100"
            >
              <div>
                <div className="font-bold text-gray-900">Teknisk SEO & Sitemap</div>
                <div className="text-[11px] text-gray-500">Schema markup, IndexNow & sitemap</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <a
              href={market === "NO" ? "https://simplyloan.no/artikler/" : "https://simplyloan.se/guide"}
              target="_blank"
              rel="noreferrer"
              className="w-full py-2.5 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs rounded-xl transition flex items-center justify-center gap-1.5"
            >
              <span>Besøg Live Site ({market === "NO" ? "simplyloan.no" : "simplyloan.se"})</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

      </div>

      {pusherState && <PusherTimeline market={market} state={pusherState} />}

      {overview && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-3">
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span>Åbne alarmer ({overview.openAlerts})</span>
            </h3>
            {overview.alerts.length === 0 ? (
              <p className="text-xs text-gray-500">Ingen åbne alarmer.</p>
            ) : (
              <ul className="space-y-2 text-xs">
                {overview.alerts.slice(0, 8).map((a) => (
                  <li key={a.id} className="flex items-start gap-2">
                    <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${a.severity === "critical" ? "bg-red-500"
                      : a.severity === "warning" ? "bg-amber-500" : "bg-slate-300"}`} />
                    <span className="text-gray-800">{a.title}
                      <span className="text-gray-400"> · {fmtDateTime(a.last_seen_at, tz)}{a.occurrences > 1 ? ` · ×${a.occurrences}` : ""}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-3 text-xs">
            <h3 className="font-bold text-gray-900 text-sm">Motor & forbrug</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(overview.switches).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-2">
                  <span className="text-gray-500 capitalize">{k}</span>
                  <span className={v === "live" ? "font-semibold text-[#0F3F34]" : "text-amber-700"}>{MODE_LABEL[v] || v}</span>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t border-gray-100 space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">DataForSEO denne md.</span>
                <span>${overview.spend.dataforseoUsd.toFixed(2)} / ${overview.spend.dataforseoBudgetUsd.toFixed(0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Bedrock (estimat)</span>
                <span>${overview.spend.bedrockUsdEstimate.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">GSC 28 dage</span>
                <span>{overview.gsc.clicks28d.toLocaleString("da-DK")} klik · {overview.gsc.impressions28d.toLocaleString("da-DK")} visn.</span></div>
            </div>
            <div className="pt-2 border-t border-gray-100 space-y-1">
              {Object.entries(overview.jobs).map(([job, j]) => (
                <div key={job} className="flex justify-between gap-2">
                  <span className="text-gray-500">{JOB_LABEL[job] || job}</span>
                  <span className={j.status === "error" ? "text-red-700" : j.status === "ok" ? "text-gray-700" : "text-amber-700"}
                        title={j.error || undefined}>
                    {j.status} · {fmtDateTime(j.finishedAt || j.startedAt, tz)}
                  </span>
                </div>
              ))}
              {Object.keys(overview.jobs).length === 0 && <p className="text-gray-500">Ingen jobkørsler endnu.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Top 5 Keywords Preview */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span>Nøgle-Søgeord i Google ({marketNames[market].flag} {marketNames[market].name})</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Top søgetermer med estimeret organisk position og søgevolumen.
            </p>
          </div>
          <button
            onClick={() => onTabChange("keywords")}
            className="text-xs font-bold text-[#0F3F34] hover:underline flex items-center gap-1"
          >
            <span>Se alle søgeord</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50/80 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="py-2.5 px-4">Søgeord</th>
                <th className="py-2.5 px-3">Position</th>
                <th className="py-2.5 px-3">Månedlig Volumen</th>
                <th className="py-2.5 px-3">Sværhedsgrad (KD)</th>
                <th className="py-2.5 px-3">Målside</th>
                <th className="py-2.5 px-4 text-right">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs">
              {topKeywords.map((kw) => (
                <tr key={kw.id} className="hover:bg-gray-50/60">
                  <td className="py-3 px-4 font-bold text-gray-900">{kw.keyword}</td>
                  <td className="py-3 px-3">
                    <span className="font-mono font-bold text-[#0F3F34] bg-emerald-50 px-2 py-0.5 rounded">
                      #{kw.currentPosition}
                    </span>
                  </td>
                  <td className="py-3 px-3 font-semibold text-gray-700">
                    {kw.searchVolume.toLocaleString('da-DK')} / md.
                  </td>
                  <td className="py-3 px-3">
                    <span className={`px-2 py-0.5 rounded font-bold ${
                      kw.difficulty < 40 ? 'bg-emerald-100 text-emerald-800' :
                      kw.difficulty < 65 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                    }`}>
                      KD {kw.difficulty}
                    </span>
                  </td>
                  <td className="py-3 px-3 font-mono text-gray-500">{kw.targetUrl}</td>
                  <td className="py-3 px-4 text-right">
                    {kw.change > 0 ? (
                      <span className="text-emerald-600 font-bold">▲ +{kw.change}</span>
                    ) : kw.change < 0 ? (
                      <span className="text-red-500 font-bold">▼ {kw.change}</span>
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
